/**
 * Состав единой легенды карты.
 *
 * Владелец 2026-08-07: «всю информацию о легендах для активных оверлеев выводить
 * не отдельно для каждого в разных областях карты, а всё внутри одной плашки —
 * справа снизу». До этого подписи изотермы и фронтов висели сверху по центру,
 * волнение и землетрясения — справа снизу, Луна — ещё ниже: собрать взглядом,
 * чем раскрашена карта, было нельзя.
 *
 * Здесь — чистая часть: КАКИЕ строки показывать и что на них написано. Рисование
 * (панель на карте) и печать (плашка на скачанной картинке) берут этот же список,
 * поэтому экран и файл не могут разойтись.
 */
import { formatNumber, getLang, t } from '../i18n'
import { ISOBATH_SWATCH } from '../map/isobathStep'
import { districtColor } from '../map/districts'
import { banColor } from '../map/bans'
import { eezStyle } from '../map/eez'
import { substrateClassKey, substrateColor } from '../map/substrate'
import { isFiring, navwarnColor, navwarnKindKey } from '../map/navwarn'
import { earthquakeColor } from '../map/layerOrder'
import { MARK_COLOR } from '../models/nnMarksLayer'
import { WAVE_HS_MIN, WAVE_HS_MAX } from '../models/arrows'
import {
  frontSwatch,
  isothermSurfaceSwatch,
  isothermSwatch,
  swatchBackground,
  WAVE_SWATCH,
} from './overlaySwatch'
import { moonPhase } from './moon'
import { waveLegendLabel, type WaveSource } from './waveLabel'
import type { FrontUnit } from './frontsMath'

/** Адрес живой карты. Стоит подписью под легендой — и на экране, и на
 *  скачанной картинке (владелец 2026-08-08): кадр уходит наружу пересылкой, и
 *  без адреса дороги обратно в сервис из него нет. */
export const SERVICE_URL = 'https://rybprognoz.ru/app/'

export type LegendMark =
  | { kind: 'line'; color: string; casing: string }
  | { kind: 'ramp'; gradient: string; stops: string[] }
  | { kind: 'dot'; color: string }
  | { kind: 'emoji'; glyph: string }

export interface LegendRow {
  id: string
  mark: LegendMark
  text: string
  /** Приписка помельче: счётчик, освещённость. */
  note?: string
}

export interface LegendState {
  isotherm: number | null
  /** Градус поверхностной изотермы (0 м); null — выключена. */
  isothermSurface?: number | null
  /** Шаг смежных изотерм, °C; null — выключены. */
  isothermSpread?: number | null
  fronts: number | null
  frontsUnit: FrontUnit
  isobathStep: number | null
  waveOverlay: boolean
  waveSource?: WaveSource
  /** Есть ли сетка SST в кадре: без неё изотерма и фронты рисуют пустоту. */
  sstData?: boolean
  /** Поставка SST закрывает кадр не целиком — линии оборвутся по кромке данных. */
  sstPartial?: boolean
  /** Есть ли поле волнения в кадре. */
  waveData?: boolean
  /** Есть ли сетка ПОВЕРХНОСТИ в кадре. Отдельно от `sstData`: глубинной сетки
   *  может не быть вовсе (её инструменты выключены), и общий признак наврал бы
   *  строке о поверхности. */
  surfaceData?: boolean
  /** Легла ли поверхностная линия на карту. */
  isothermSurfaceDrawn?: boolean
  /** Легло ли на карту хоть что-то. Данные могут быть все, а порог — за
   *  пределами поля: 5-градусной изотермы нет в воде 7,6…11,5 °C, изобат через
   *  10 м нет там, где мельче 1460 м не бывает. `undefined` — не знаем. */
  isothermDrawn?: boolean
  frontsDrawn?: boolean
  isobathsDrawn?: boolean
  waveDrawn?: boolean
  /** Включён ли слой границ ИЭЗ. */
  eez?: boolean
  fishingDistricts?: boolean
  /** Нарисовал ли слой районы. `false` — в кадре их нет (вне ДВ районов нет
   *  вовсе), и строка с российской оговоркой была бы ложью; `undefined` — не
   *  знаем, строка остаётся. */
  districtsDrawn?: boolean
  /** Нарисовал ли слой запретов заливку. */
  bansDrawn?: boolean
  /** Оговорка запретов — РЕГИОНА кадра (`meta.disclaimer_key` ручки): у Омана
   *  своя, у приказа № 285 своя. */
  bansDisclaimerKey?: string | null
  /** Запреты на всю акваторию, действующие сейчас для вида: заливки у них нет
   *  (залитая ИЭЗ спрятала бы закрытые районы), а назвать их обязаны. */
  bansEverywhere?: string[]
  /** Тип дна: классы, легшие в кадр. Пусто или нет — строк нет: легенда
   *  называет только то, что нарисовано. */
  substrate?: number[]
  /** Навигационные предупреждения: роды, легшие в кадр. Строка на род — цвет
   *  зоны и есть её смысл (стрельбы красные). */
  navwarn?: string[]
  /** Сколько мест нейрокарты нарисовано; 0 или нет — строки нет. */
  nnMarks?: number
  /** Сколько землетрясений в кадре; 0 — слой выключен или их нет. */
  earthquakes: number
  moon: boolean
  date: string
}

const num = (v: number) => formatNumber(getLang(), v, 1)

/** Приписка про пустоту. `undefined` (признак не передали) молчим: старые
 *  вызовы не должны начать обещать отсутствие данных.
 *
 *  Три разные новости, и порядок между ними важен:
 *    «нет данных»         — сетки в кадре нет вовсе;
 *    «данные не на весь кадр» — поставка обрывается внутри кадра (прод 12.08.2026:
 *      SST до 162,67° в.д., изотермы срезаны по прямой, а легенда молчала, потому
 *      что признак наличия истинен от ОДНОЙ непустой ячейки);
 *    «в кадре нет»        — поле полное, но порог лежит за его пределами.
 *
 *  Обрыв поставки старше «в кадре нет»: порог мог не встретиться именно потому,
 *  что половину кадра не привезли. Без сетки оба были бы враньём — мы попросту
 *  не смотрели. */
function noteEmpty(
  hasData: boolean | undefined,
  drawn?: boolean | undefined,
  partial?: boolean | undefined,
): string | undefined {
  if (hasData === false) return t('maps.legend.noData')
  if (partial === true) return t('maps.legend.partialData')
  if (drawn === false) return t('maps.legend.notInView')
  return undefined
}
/** Единица градиента температуры: перепад на 100 или на 10 км. */
export const frontUnitLabel = (u: FrontUnit) =>
  u === '10km' ? t('maps.fronts.unit10km') : t('maps.fronts.unit100km')

/** Строки легенды в порядке показа. Пустой список = плашки нет вовсе. */
export function legendRows(s: LegendState): LegendRow[] {
  const rows: LegendRow[] = []

  if (s.isotherm != null) {
    rows.push({
      id: 'isotherm',
      mark: { kind: 'line', color: isothermSwatch().line, casing: isothermSwatch().casing },
      text: t('maps.legend.isotherm', { value: num(s.isotherm) }),
      note: noteEmpty(s.sstData, s.isothermDrawn, s.sstPartial),
    })
  }
  // Смежные и поверхность — строками рядом с основной, а не приписками к ней:
  // на карте это ОТДЕЛЬНЫЕ линии, и поверхностная вдобавок другого цвета, то
  // есть обязана иметь свой образец (правило плашки «слева цвет, справа текст»).
  if (s.isotherm != null && s.isothermSpread != null) {
    rows.push({
      id: 'isotherm-spread',
      mark: { kind: 'line', color: isothermSwatch().line, casing: isothermSwatch().casing },
      text: t('maps.legend.isothermSpread', { step: num(s.isothermSpread) }),
    })
  }
  if (s.isotherm != null && s.isothermSurface != null) {
    rows.push({
      id: 'isotherm-surface',
      mark: {
        kind: 'line',
        color: isothermSurfaceSwatch().line,
        casing: isothermSurfaceSwatch().casing,
      },
      text: t('maps.legend.isothermSurface', { value: num(s.isothermSurface) }),
      // Своя сетка — своя правда о пустоте; охват поставки общий (обрыв — это
      // свойство даты и района, а не горизонта).
      note: noteEmpty(s.surfaceData, s.isothermSurfaceDrawn, s.sstPartial),
    })
  }
  if (s.fronts != null) {
    rows.push({
      id: 'fronts',
      mark: { kind: 'line', color: frontSwatch().line, casing: frontSwatch().casing },
      text: t('maps.legend.fronts', {
        value: num(s.fronts),
        unit: frontUnitLabel(s.frontsUnit),
      }),
      note: noteEmpty(s.sstData, s.frontsDrawn, s.sstPartial),
    })
  }
  if (s.isobathStep != null) {
    // Изобаты владелец в списке не назвал, но зелёная линия на карте без подписи
    // — та же безымянная краска, ради которой плашку и собирали.
    rows.push({
      id: 'isobaths',
      mark: { kind: 'line', color: ISOBATH_SWATCH.line, casing: ISOBATH_SWATCH.casing },
      text: t('maps.legend.isobaths', { step: Math.round(s.isobathStep) }),
      // Потолок инструмента — 6000 м; вне покрытия таблицы линий нет вовсе.
      note: noteEmpty(undefined, s.isobathsDrawn),
    })
  }
  if (s.waveOverlay) {
    rows.push({
      id: 'waves',
      // Строка той же формы, что изотерма/фронты/изобаты (правка владельца
      // 2026-08-07): слева образец цвета, справа края шкалы — двухэтажная
      // подпись выбивалась из ряда.
      mark: { kind: 'ramp', gradient: swatchBackground(WAVE_SWATCH), stops: WAVE_SWATCH.stops },
      // Оценку по ветру за реальные волны не выдаём — подпись приходит из waveLabel.
      text: waveLegendLabel(s.waveSource),
      note: noteEmpty(s.waveData, s.waveDrawn) ?? `${num(WAVE_HS_MIN)}…≥${WAVE_HS_MAX}`,
    })
  }
  if (s.eez) {
    // ОДНА строка: спорных рубежей в «Лоции» нет (решение владельца 07.09.2026),
    // и все типы линий рисуются одним тоном — см. `map/eez.ts`. Вторая строка
    // описывала бы цвет, которого на карте больше не встретишь.
    rows.push({
      id: 'eez',
      mark: { kind: 'line', color: eezStyle('Treaty').color, casing: '#ffffff' },
      text: t('layers.eez'),
      // Оговорка источника едет ВМЕСТЕ со строкой и, значит, попадает и на
      // скачанный кадр (`legendPlateLines`): Marine Regions прямо заявляет, что
      // его геометрия не для навигации и не для юридических целей, а кадр
      // уходит наружу — в переписку, в отчёт.
      note: t('eez.disclaimer'),
    })
  }
  if (s.fishingDistricts && s.districtsDrawn !== false) {
    rows.push({
      id: 'fishing-districts',
      // Штриха в образце нет: `LegendMark` пунктира не знает, а цвет линию
      // опознаёт — тем же приёмом живёт и спорный рубеж ИЭЗ.
      mark: { kind: 'line', color: districtColor(), casing: '#ffffff' },
      text: t('layers.fishing_districts'),
      // Оговорка едет ВМЕСТЕ со строкой и, значит, попадает на скачанный кадр:
      // полигоны построены НАМИ по тексту приказа, а кадр уходит наружу — в
      // переписку, в отчёт. Та же причина, что у рубежей ИЭЗ.
      note: t('districts.disclaimer'),
    })
  }
  // Запреты — тот же тумблер, поэтому и тот же флаг: заведи им отдельное
  // поле, и равенство экрана и кадра (`exportLegendParity`) пришлось бы
  // держать ещё за одну строку.
  if (s.fishingDistricts && s.bansDrawn !== false) {
    rows.push({
      id: 'fishing-bans',
      mark: { kind: 'line', color: banColor(), casing: '#ffffff' },
      text: t('layers.fishing_bans'),
      note: t(s.bansDisclaimerKey ?? 'bans.disclaimer'),
    })
  }
  if (s.fishingDistricts && s.bansEverywhere?.length) {
    rows.push({
      id: 'fishing-bans-everywhere',
      mark: { kind: 'line', color: banColor(), casing: '#ffffff' },
      text: t('bans.everywhere'),
      note: s.bansEverywhere.join('; '),
    })
  }
  if (s.substrate?.length) {
    // Строка на класс: цвет заливки — категория, и шкалой его не назвать.
    // Оговорка источника едет с последней строкой и попадает на кадр.
    s.substrate.forEach((cls, i) => {
      rows.push({
        id: `substrate-${cls}`,
        mark: { kind: 'dot', color: substrateColor(cls) },
        text: `${t('layers.substrate')}: ${t(substrateClassKey(cls))}`,
        note: i === s.substrate!.length - 1 ? t('substrate.disclaimer') : undefined,
      })
    })
  }
  if (s.navwarn?.length) {
    // Стрельбы и пуски делят один цвет — и одну строку легенды.
    const firing = s.navwarn.filter(isFiring)
    const rest = s.navwarn.filter(k => !isFiring(k))
    const groups: Array<[string, string[]]> = [
      ...(firing.length ? [[firing[0], firing] as [string, string[]]] : []),
      ...rest.map(k => [k, [k]] as [string, string[]]),
    ]
    groups.forEach(([color, kinds], i) => {
      rows.push({
        id: `navwarn-${color}`,
        mark: { kind: 'dot', color: navwarnColor(color) },
        text: `${t('layers.navwarn')}: ${kinds.map(k => t(navwarnKindKey(k))).join(', ')}`,
        note: i === groups.length - 1 ? t('navwarn.disclaimer') : undefined,
      })
    })
  }
  if ((s.nnMarks ?? 0) > 0) {
    rows.push({
      id: 'nn-marks',
      mark: { kind: 'dot', color: MARK_COLOR },
      text: t('maps.legend.nnMarks'),
      note: String(s.nnMarks),
    })
  }
  if (s.earthquakes > 0) {
    rows.push({
      id: 'earthquakes',
      mark: { kind: 'dot', color: earthquakeColor() },
      text: t('maps.legend.earthquakes'),
      note: String(s.earthquakes),
    })
  }
  if (s.moon) {
    const phase = moonPhase(s.date)
    rows.push({
      id: 'moon',
      mark: { kind: 'emoji', glyph: phase.emoji },
      text: t(phase.nameKey).toLowerCase(),
      note: t('maps.legend.illumination', { percent: phase.illumination }),
    })
  }

  return rows
}

/** Строки для плашки на скачанной картинке: тот же список, но текстом.
 *  Цвет метки переживает печать — по нему на PNG и опознают линию. */
export type PlateMark =
  /** `dash` — пунктир: им на карте отличается прогнозная часть трека сноса. */
  | { kind: 'line'; color: string; casing?: string; dash?: boolean }
  /** `hollow` — пустая середина (замёрзший трек), `ring` — цвет обводки. */
  | { kind: 'dot'; color: string; ring?: string; hollow?: boolean }
  /** Ореол неопределённости: бледный круг с точкой в центре. */
  | { kind: 'halo'; color: string }
  | { kind: 'ramp'; stops: string[] }

export function legendPlateLines(rows: LegendRow[]): { text: string; mark?: PlateMark }[] {
  return rows.map(r => {
    const glyph = r.mark.kind === 'emoji' ? r.mark.glyph : ''
    const text = [glyph, r.text, r.note].filter(Boolean).join(' ')
    if (r.mark.kind === 'line') {
      // Линию печатаем с ореолом: графитовая изотерма без белой подложки на
      // тёмной плашке пропала бы, как и на карте без неё.
      return { text, mark: { kind: 'line', color: r.mark.color, casing: r.mark.casing } }
    }
    if (r.mark.kind === 'dot') return { text, mark: { kind: 'dot', color: r.mark.color } }
    if (r.mark.kind === 'ramp') return { text, mark: { kind: 'ramp', stops: r.mark.stops } }
    return { text }
  })
}
